import { AccessToken, AuthResponse, OauthOptions, ParsedToken, TokenInfo } from '@/store/engine/types/Auth';
import { Store } from '@/store/mainStore';
import EngineModule from '@/store/engine/modules/EngineModule';
import ClientOAuth2 from 'client-oauth2';
import { action, observable, reaction } from 'mobx';
import { MobileAppRedirectData } from '@/store/authStore';
import Cookies from 'js-cookie';
import Const from '@/Const';

type Input = {
	code?: string;
	refreshToken?: string;
};

type Split<T, K extends keyof T> = K extends unknown ? { [I in keyof T]: I extends K ? T[I] : never } : never;
type Explode<T> = Split<T, keyof T>; // Allow only one field of object

export default class AuthModule extends EngineModule {
	private readonly options: OauthOptions;

	@observable public token: AccessToken | null = null;
	@observable public rawToken: string = '';
	@observable public tokenExpiresAt: number = 0;

	@observable public refreshToken: string = '';
	@observable public refreshTokenExpiresAt: number = 0;

	@observable public sessionEndsAt: number = 0;

	constructor(options: OauthOptions, store: Store) {
		super(store);
		this.options = options;

		reaction(
			() => this.tokenExpiresAt,
			(t) => {
				t && this.autoRefreshToken();
			}
		);
	}

	public async logout() {
		localStorage.removeItem('storeID');
		localStorage.removeItem('refresh_token');
		localStorage.removeItem('refresh_expiration');
		Cookies.remove('mobile-app-redirect-data');
		const url = await this.revokeToken();
		location.replace(url.logout_url);
	}

	@action
	public getLoginUri(siteID?: string, scopes?: Array<string>) {
		const engineAuthState = 'PODAPP|' + new Date().getTime().toString();
		let optional = {};
		if (siteID && siteID !== '') {
			Object.assign(optional, { query: { store_id: siteID } });
		}
		if (scopes && scopes.length > 0) {
			Object.assign(optional, { scopes: scopes });
		}
		const customerAuth = new ClientOAuth2({
			clientId: this.options.engineClientID,
			accessTokenUri: this.options.accessTokenUri,
			authorizationUri: this.options.authorizationUri,
			redirectUri: this.options.redirectUri,
			state: engineAuthState,
			...optional
		});

		return customerAuth.code.getUri();
	}

	@action
	public async getToken<T extends Input>(input: Explode<T>): Promise<AccessToken> {
		//hack for landmark redirect due to double questionmark
		if (this.options.redirectUri.includes('?code') && this.options.redirectUri.includes('landmarks')) {
			this.options.redirectUri = this.options.redirectUri.split('?code')[0];
		}

		const body = new URLSearchParams();
		body.set('client_id', this.options.engineClientID);
		body.set('redirect_uri', this.options.redirectUri);
		body.set('grant_type', input['code'] ? 'authorization_code' : 'refresh_token');
		body.set(input['code'] ? 'code' : 'refresh_token', input.code! || input.refreshToken!);
		try {
			const baseTimeMs = Date.now(); // NOTE: Might need to detect clock-skew
			const response = await fetch(`${this.options.accessTokenUri}`, {
				method: 'POST',
				body: body
			});
			const result = (await response.json()) as TokenInfo;
			this.token = {
				jwt: result.access_token,
				originalResponse: result,
				parsedToken: this.parseJwt(result.access_token)
			};
			this.rawToken = result.access_token;
			this.tokenExpiresAt = baseTimeMs + result.expires_in * 1000;
			this.refreshTokenExpiresAt = (baseTimeMs + Const.OAUTH_REFRESH_TOKEN_DURATION_MS) / 1000;
			this.sessionEndsAt = baseTimeMs + Const.OAUTH_SESSION_DURATION_MS;

			if (result.refresh_token) {
				this.refreshToken = result.refresh_token;
				localStorage.setItem('refresh_expiration', `${this.refreshTokenExpiresAt}`);
				localStorage.setItem('refresh_token', `${this.refreshToken}`);
				const storeIDInCookie = JSON.parse(Cookies.get('mobile-app-redirect-data') || '{}').storeID;
				const storeID = localStorage.getItem('storeID');
				const mobileAppCookie: MobileAppRedirectData = {
					refreshToken: this.refreshToken,
					refreshTokenExpiresAt: this.refreshTokenExpiresAt,
					storeID: storeIDInCookie || storeID
				};

				Cookies.set('mobile-app-redirect-data', JSON.stringify(mobileAppCookie));
			}
			return new Promise((r) => r(this.token as AccessToken));
		} catch (e) {
			throw new Error(e);
		}
	}

	@action
	public async fillStateWithExternalJWT(jwt: string) {
		this.rawToken = jwt;
		this.token = {
			jwt: jwt,
			originalResponse: {
				access_token: jwt,
				refresh_token: '',
				refresh_token_expires_at: 0,
				expires_at: 0,
				expires_in: 900,
				session_ends_at: 0,
				token_type: 'Bearer'
			},
			parsedToken: this.parseJwt(jwt)
		};
		await this.store.engine.setEngineClient(jwt);
		return this.token;
	}

	@action
	private async autoRefreshToken() {
		const r = async () => {
			const res = await this.exchangeRefreshToken();

			await this.store.engine.setEngineClient(res.originalResponse.access_token);
			document.title = 'ARIZE';
			this.store.auth.operator.setLoggedIn(true);
		};

		if (this.token && this.token.parsedToken) {
			setTimeout(async () => {
				try {
					r();
				} catch (e) {
					localStorage.removeItem('refresh_token');
					localStorage.removeItem('refresh_expiration');
					throw new Error(e);
				}
			}, this.tokenExpiresAt - new Date().getTime() - 60000);
		} else {
			throw new Error('JWT is missing');
		}
	}

	@action
	private async getJWT(code: string): Promise<AccessToken | undefined> {
		return await this.getToken({ code });
	}

	@action
	public async exchangeRefreshToken(): Promise<AccessToken> {
		let refreshToken = this.refreshToken;

		const mobileAppRedirectData = Cookies.get('mobile-app-redirect-data');
		if (mobileAppRedirectData) {
			const object = JSON.parse(mobileAppRedirectData) as MobileAppRedirectData;
			refreshToken = object.refreshToken;
		}

		return await this.getToken({ refreshToken });
	}

	@action
	private async revokeToken(): Promise<{ logout_url: string }> {
		const body = new URLSearchParams();
		body.set('client_id', this.options.engineClientID);
		body.set('redirect_uri', this.options.redirectUri);
		if (this.token?.originalResponse.access_token) {
			body.set('token', `${this.token?.originalResponse.access_token}`);
		}
		body.set('token_type_hint', 'access_token');

		const url = new URL(this.options.logoutUri);
		url.search = body.toString();
		return {
			logout_url: url.toString()
		};
	}

	private parseJwt(token: string): ParsedToken {
		const base64Url = token.split('.')[1];
		const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
		const jsonPayload = decodeURIComponent(
			atob(base64)
				.split('')
				.map(function(c) {
					return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
				})
				.join('')
		);

		return JSON.parse(jsonPayload);
	}
}
